import { useEffect } from 'react';

// ── SCROLL DIRECTION ─────────────────────────────────────────────────────────
// Stamps `data-scroll="up" | "down"` on <html> so the sticky chrome (site
// header, back bar) can slide away while you read and come back the moment you
// scroll up — the pattern every mobile app uses to buy back vertical space.
//
// It writes an ATTRIBUTE rather than returning state on purpose. Six pages
// render a header; making each one subscribe would re-render six trees on every
// scroll frame. One attribute lets CSS do the work with no React involved.
//
// Deliberately NOT gated on width here — the attribute is always current, and
// the stylesheet decides that only screens under 900px act on it. A media query
// in CSS is free; a JS listener that has to re-evaluate on resize is not.
const THRESHOLD = 6;    // ignore sub-pixel jitter and trackpad noise
const TOP_ZONE = 80;    // near the top the chrome is always shown

export default function useScrollDirection() {
  useEffect(() => {
    const root = document.documentElement;
    let last = window.scrollY;
    let ticking = false;

    const apply = () => {
      ticking = false;
      const y = window.scrollY;

      // At the top there is nothing to hide behind, and iOS rubber-banding
      // reports negative offsets that would otherwise read as "scrolling up".
      if (y < TOP_ZONE) { root.setAttribute('data-scroll', 'up'); last = y; return; }

      const delta = y - last;
      if (Math.abs(delta) < THRESHOLD) return;   // leave `last` alone: small
                                                 // moves must accumulate, or a
                                                 // slow drag never registers
      root.setAttribute('data-scroll', delta > 0 ? 'down' : 'up');
      last = y;
    };

    // rAF-coalesced: scroll fires far more often than the screen repaints, and
    // measuring scrollY in the handler itself forces layout every event.
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(apply);
    };

    root.setAttribute('data-scroll', 'up');
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      root.removeAttribute('data-scroll');
    };
  }, []);
}
